import type { VaultEntry, Prompt, Ritual, Project, SiteSettings } from "@akhshy/types";

/** Placeholder portrait SVG (data URI) from the mockup — used when settings.portraitUrl is empty. */
export const PORTRAIT_PLACEHOLDER =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 220'%3E%3Crect width='200' height='220' fill='%23E7DFC9'/%3E%3Ccircle cx='158' cy='40' r='34' fill='%23DCCFA8'/%3E%3Crect x='0' y='168' width='200' height='52' fill='%235F6B4E'/%3E%3Cpath d='M100 128c-34 0-58 20-64 44v48h128v-48c-6-24-30-44-64-44z' fill='%233E5C74'/%3E%3Cpath d='M84 132h32v14c0 10-7 16-16 16s-16-6-16-16z' fill='%23A9714B'/%3E%3Cpath d='M86 118h28v22c0 4-6 8-14 8s-14-4-14-8z' fill='%23935F3D'/%3E%3Cellipse cx='100' cy='84' rx='38' ry='42' fill='%23A9714B'/%3E%3Cpath d='M62 78c-2-30 16-48 38-48s40 18 38 48c-2-6-4-10-8-14-2 4-4 6-8 8 1-6 0-10-3-14-8 8-28 12-44 8-4 4-7 8-8 14-3-4-4-8-5-2z' fill='%232E2A24'/%3E%3Cpath d='M62 76c-3 2-6 8-4 14 1 5 4 8 7 8z' fill='%23A9714B'/%3E%3Cpath d='M138 76c3 2 6 8 4 14-1 5-4 8-7 8z' fill='%23A9714B'/%3E%3Ccircle cx='84' cy='88' r='11' fill='none' stroke='%232E2A24' stroke-width='3'/%3E%3Ccircle cx='116' cy='88' r='11' fill='none' stroke='%232E2A24' stroke-width='3'/%3E%3Cpath d='M95 88h10' stroke='%232E2A24' stroke-width='3'/%3E%3Ccircle cx='84' cy='89' r='3' fill='%232E2A24'/%3E%3Ccircle cx='116' cy='89' r='3' fill='%232E2A24'/%3E%3Cpath d='M92 112c4 4 12 4 16 0' fill='none' stroke='%237A4A2B' stroke-width='3' stroke-linecap='round'/%3E%3Cpath d='M97 102c2 1.5 4 1.5 6 0' fill='none' stroke='%23935F3D' stroke-width='2.5' stroke-linecap='round'/%3E%3Cpath d='M78 150l10 14 12-10 12 10 10-14' fill='none' stroke='%23F7F3EA' stroke-width='4' stroke-linejoin='round'/%3E%3C/svg%3E";

export const FALLBACK_SETTINGS: SiteSettings = {
  eyebrow: "Akhshy Ganesh",
  headline: "AI engineer,",
  headlineAccent: "building in public.",
  heroSub:
    "This is my desk on the internet — the tools I'm making, the prompts I keep reaching for, and the weekly notes on what's actually working.",
  deskNote: "still figuring it out —\nwriting it down anyway",
  nowPills: [
    { label: "Shipping envault" },
    { label: "Writing a weekly ritual" },
    { label: "Collecting prompts" },
  ],
  portraitUrl: "/portrait.webp",
  portraitCaption: "that's me — akhshy",
  footerNote: "Thanks for stopping by the desk.",
  updatedAt: "2026-07-05T00:00:00.000Z",
};

const base = (id: string, sortOrder: number) => ({
  id,
  slug: id,
  tags: [] as string[],
  published: true,
  sortOrder,
  createdAt: "2026-07-05T00:00:00.000Z",
  updatedAt: "2026-07-05T00:00:00.000Z",
});

export const FALLBACK_VAULT: VaultEntry[] = [
  {
    ...base("note-docker", 0),
    kind: "note",
    title: "docker-cleanup.md",
    filename: "docker-cleanup.md",
    description:
      "The disk-space rescue routine I run every few months, written down so I stop re-googling it.",
    url: null,
    content: `# Docker cleanup routine

My laptop was down to **4 GB free** again, so writing this down once and for all.

## The quick pass

Run these in order, least destructive first:

- \`docker system df\` — see what's actually eating space before deleting anything
- \`docker container prune\` — dead containers, always safe
- \`docker image prune\` — dangling images only
- \`docker builder prune\` — build cache, usually the biggest win on my machine

## The nuclear option

\`docker system prune -a --volumes\` removes **everything not attached to a running container**, including named volumes. I lost a local Postgres data volume to this once. Never again — check \`docker volume ls\` first and back up anything with a name you recognize.

## What I learned the hard way

The build cache regrows fast if you rebuild images with \`--no-cache\` out of habit. Stop doing that. Layer caching is the whole point — structure the Dockerfile so dependencies install before source copy, and rebuilds cost almost nothing.

See also [Docker's own prune docs](https://docs.docker.com/config/pruning/) — shorter than this note, less honest about the volume incident.`,
  },
  {
    ...base("note-llm-eval", 1),
    kind: "note",
    title: "llm-eval-notes.md",
    filename: "llm-eval-notes.md",
    description:
      "What I've learned about evaluating LLM features before shipping them — graders, sample sizes, traps.",
    url: null,
    content: `# LLM eval notes

Collected from shipping three LLM features this year. **Evals are the tests you write before you trust the vibe.**

## Start embarrassingly small

- 20 hand-written cases beat 0 automated ones. Start there.
- Write the cases from real failures, not imagined ones. Every bug report becomes an eval row.
- Keep them in a plain \`evals.jsonl\` in the repo — no platform needed until it hurts.

## Grading

Three tiers, in order of preference:

- **Exact / programmatic checks** — does the JSON parse, is the field present, is the number in range. Cheap, deterministic, use everywhere you can.
- **Model-graded with a rubric** — works, but write the rubric like you'd brief a new hire: one criterion per question, yes/no answers.
- **Human review** — reserve for the 10% where the first two disagree.

## Traps I fell into

- Grading against my *favorite* output instead of an *acceptable* one. The eval failed every rephrasing that was fine.
- Changing the prompt and the eval set in the same commit. You can't tell what moved the number.
- Trusting a 5-sample run. Variance at small n is brutal — I now run 3 seeds minimum before believing any delta.

## The one-liner I keep repeating

If you can't say what "worse" looks like, you're not ready to change the prompt.`,
  },
  {
    ...base("note-career", 2),
    kind: "note",
    title: "career-questions.md",
    filename: "career-questions.md",
    description:
      "Questions I ask myself every quarter, and the ones I ask in every interview — both directions.",
    url: null,
    content: `# Career questions

A note I revisit every quarter. Borrowed from many people, sharpened by using them.

## Questions I ask myself

- What did I ship in the last 90 days that someone else *uses*?
- Am I learning at the rate the field is moving, or coasting on last year's stack?
- If my job disappeared tomorrow, is my public work enough to open the next door?
- What am I doing only because it looks good, not because it compounds?

## Questions I ask in interviews (as the candidate)

- Show me the last incident. Who wrote the postmortem and what changed after?
- What does a **normal Tuesday** look like for the person in this role?
- Who decides what gets built — and how often has an engineer changed that decision?

## Questions I ask when I'm the interviewer

- Tell me about something you built that failed. What did you keep from it?
- What's the smallest thing you've shipped that you're proud of?

## The standing rule

Optimize for *rate of learning* until it stops compounding, then optimize for *leverage*. Re-evaluate which mode I'm in every quarter — this note is the reminder.`,
  },
  {
    ...base("note-writing", 3),
    kind: "note",
    title: "writing-checklist.md",
    filename: "writing-checklist.md",
    description:
      "The pass I do on every ritual post before publishing. Ten minutes, catches ninety percent of the cringe.",
    url: null,
    content: `# Writing checklist

The ten-minute pass before any ritual post goes out.

## Structure

- Does the first sentence say something, or is it throat-clearing? Delete until it does.
- One idea per post. If there are two, that's next week's post — move it to the drafts folder.
- Headings should be skimmable as a summary on their own.

## Voice

- Read it aloud once. Anywhere I stumble, the reader stumbles harder.
- Replace every **"leverage"**, **"utilize"**, and **"delve"** — if I wouldn't say it to a friend at lunch, it doesn't ship.
- Keep one specific number or concrete detail per section. Specifics build trust; adjectives don't.

## Honesty

- Did I claim more certainty than I have? Add the caveat, keep it short.
- Am I telling the failure too? The posts people reply to are the ones with the scar tissue in them.

Last edited after week 26 — the post about deleting code got triple the replies. The checklist item about scar tissue exists because of it.`,
  },
  {
    ...base("tool-excalidraw", 4),
    kind: "tool",
    title: "Excalidraw",
    filename: null,
    description:
      "Whiteboard sketches that look hand-drawn. I use it for every architecture diagram before writing a line of code.",
    content: null,
    url: "https://excalidraw.com",
  },
  {
    ...base("read-agents", 5),
    kind: "read",
    title: "Building effective agents — Anthropic",
    filename: null,
    description:
      "The clearest mental model I've found for when to use workflows vs. autonomous agents. I re-read this before every agent project.",
    content: null,
    url: "https://www.anthropic.com/research/building-effective-agents",
  },
];

export const FALLBACK_PROMPTS: Prompt[] = [
  {
    ...base("prompt-review", 0),
    useCase: "Code review",
    title: "The skeptical reviewer",
    body: `ROLE
You are a senior engineer reviewing a pull request from a competent
colleague. You are skeptical by default: you assume the code has at
least one real problem and your job is to find it.

CONTEXT
I will paste a diff below. The codebase conventions are whatever the
surrounding unchanged lines imply — infer them, don't invent your own.
Assume tests exist unless the diff shows otherwise.

WHAT TO LOOK FOR (in priority order)
1. Correctness bugs: wrong logic, inverted conditions, off-by-one,
   broken error paths, race conditions.
2. Edge cases the code silently mishandles: empty input, null,
   unicode, timezone boundaries, concurrent calls.
3. Misleading elements: names that lie, comments that no longer match
   the code, functions that do more than their name says.
4. Security: injection, unvalidated input crossing a trust boundary,
   secrets in code.

CONSTRAINTS
- Do NOT comment on formatting, import order, or style preferences.
- Do NOT suggest rewrites of code the diff doesn't touch.
- If you find nothing in a category, say so in one line — don't
  invent problems to seem thorough.
- Maximum 8 findings. If you have more, keep the 8 most severe.

OUTPUT FORMAT
For each finding:
- [SEVERITY: high/medium/low] file:line
- One sentence: what is wrong.
- One sentence: the concrete fix.
End with a single verdict line: MERGE / MERGE AFTER FIXES / DO NOT MERGE.

Here is the diff:`,
  },
  {
    ...base("prompt-dejargon", 1),
    useCase: "Writing",
    title: "The de-jargonizer",
    body: `ROLE
You are an editor who makes technical writing sound like a smart
person talking, not a company announcing. Your taste: Paul Graham's
plainness, but warmer.

CONTEXT
I will paste a draft below. It's for my personal site — readers are
engineers and curious non-engineers. The draft is probably 30% too
long and hiding its best sentence in the middle.

YOUR JOB, IN THREE PASSES
Pass 1 — Cut:
- Delete throat-clearing openers ("In today's fast-paced world...").
- Delete every sentence that doesn't add a fact, a feeling, or a joke.
- Kill these words on sight: leverage, utilize, delve, seamless,
  robust, game-changing, in the realm of.
Pass 2 — Clarify:
- Replace abstract claims with the concrete detail behind them.
  "Improved performance significantly" → the actual number.
- Break any sentence over 25 words into two.
- Active voice unless the actor is genuinely unknown.
Pass 3 — Warm it up:
- Keep my first-person voice and any self-deprecating asides.
- The reader should feel written-to, not broadcast-at.

CONSTRAINTS
- Keep every fact. If a fact seems wrong, flag it in [brackets],
  don't silently fix it.
- Don't add new claims or examples I didn't make.
- Preserve markdown structure (headings, lists, links).

OUTPUT FORMAT
1. The edited draft, ready to publish.
2. A short "what I cut and why" list — max 5 bullets.
3. The single best sentence in the piece, quoted. If it was buried,
   tell me where it should move.

Here is the draft:`,
  },
  {
    ...base("prompt-duck", 2),
    useCase: "Debugging",
    title: "The rubber duck with opinions",
    body: `ROLE
You are a debugging partner. Not a fix generator — a partner. Your
core belief: most debugging time is wasted confirming a wrong theory,
so your first job is to attack my theory, not the bug.

CONTEXT
I will describe: the symptom, when it started, what I've already
tried, and my current theory of the cause. My description will be
incomplete in ways I can't see — that's the point.

PROCESS (follow strictly, in order)
Step 1 — Restate:
Summarize the bug in two sentences to prove you understood it. If my
description is ambiguous, resolve the ambiguity by asking, not by
assuming.

Step 2 — Falsify:
Ask me the THREE questions most likely to prove my current theory
wrong. Good falsifying questions are cheap to answer and split the
possibility space in half. Do not propose any fix yet.

Step 3 — Wait:
Stop after the three questions. Do not continue until I answer.

Step 4 — After my answers:
- Rank the three most probable causes, with a confidence percentage
  and the reasoning for each.
- For the top cause, give me the single cheapest experiment that
  would confirm or kill it (a log line, a curl, a unit test).

CONSTRAINTS
- Never say "have you tried turning it off and on again" unless the
  evidence genuinely points to state corruption.
- If my "what I've tried" list already rules something out, don't
  suggest it again.
- Prefer experiments over explanations. Every reply should end with
  something I can DO in under five minutes.

Here's the bug:`,
  },
  {
    ...base("prompt-scope", 3),
    useCase: "Planning",
    title: "The scope shrinker",
    body: `ROLE
You are a pragmatic product engineer whose superpower is shipping
small. You have seen a hundred side projects die of scope, and you
are mildly allergic to the word "platform".

CONTEXT
I will describe a feature or project idea below. I am one person,
building nights and weekends, with roughly 6 focused hours per week.
Assume my estimates are 2x optimistic, because they are.

YOUR JOB
1. The kernel:
   State, in one sentence, the single user problem this idea solves.
   If the idea contains more than one problem, pick the sharpest and
   park the rest.
2. The one-week version:
   Design the smallest version a real user could benefit from within
   ONE week of my actual hours (so: ~6 hours of work). Ruthless
   choices expected — hardcode, use a spreadsheet as the database,
   skip auth, whatever it takes.
3. The leave-out list:
   Name what we are deliberately NOT building, and for each item, the
   condition that would justify building it later. Format:
   "SKIP <thing> UNTIL <observable signal>".
4. The kill criteria:
   Define what result after 2 weeks of real usage would mean this
   idea should be abandoned. Be specific enough that I can't weasel
   out of it.

CONSTRAINTS
- No new frameworks or services I'd have to learn this week.
- If the one-week version still exceeds 6 hours, shrink it again and
  tell me what you cut on the second pass.
- Don't cheerlead. If the kernel is weak, say so before designing
  anything.

OUTPUT FORMAT
Four sections with the headers above, then one closing line: the
first 30-minute task I should do tonight.

Here is the idea:`,
  },
];

export const FALLBACK_RITUALS: Ritual[] = [
  {
    ...base("ritual-27", 0),
    week: 27,
    title: "Week 27 — Shipping is a habit, not an event",
    displayDate: "Jul 3, 2026",
    content: `Spent most of the week on envault's secret rotation flow. The lesson that keeps repeating: the feature is done when someone else can use it without me in the room, not when the tests pass.

Also started drafting the prompts page for this site — turns out curating your own prompts is a brutal exercise in admitting which ones you actually use.`,
  },
  {
    ...base("ritual-26", 1),
    week: 26,
    title: "Week 26 — Deleting code felt better than writing it",
    displayDate: "Jun 26, 2026",
    content: `Removed two half-finished features from aliasmate that nobody (including me) used. The repo got smaller, the README got honest, and issues stopped feeling like debt.

Note to self: a public project is a promise. Fewer promises, kept, beats many promises, pending.`,
  },
  {
    ...base("ritual-25", 2),
    week: 25,
    title: "Week 25 — Agents don't need more autonomy, they need better desks",
    displayDate: "Jun 19, 2026",
    content: `Rebuilt an internal agent workflow from "one big autonomous loop" into three small, checkable steps. Same model, half the failures. Structure beats cleverness again.

The metaphor I keep coming back to: don't hire a genius and give them a messy desk. Clean the desk first.`,
  },
];

export const FALLBACK_PROJECTS: Project[] = [
  {
    ...base("project-envault", 0),
    tag: "Open source",
    name: "envault",
    description:
      "A tool for managing secrets and environment variables without the usual dread. Encrypt, share with your team, rotate without a spreadsheet.",
    markerNote: "currently shipping — v0.5.0",
    links: [
      { label: "GitHub →", url: "https://github.com/akhshyganesh/envault" },
      { label: "Build notes →", url: "/ritual" },
    ],
  },
  {
    ...base("project-aliasmate", 1),
    tag: "CLI",
    name: "aliasmate",
    description:
      "Keeps your shell aliases organized, synced, and actually memorable. Born from losing the same alias three laptops in a row.",
    markerNote: "stable — trimmed & tidy",
    links: [
      { label: "GitHub →", url: "https://github.com/akhshyganesh/aliasmate" },
      { label: "Build notes →", url: "/ritual" },
    ],
  },
];
